<template>
  <div class="admin-shell">
    <header class="admin-shell__header">
      <div class="admin-shell__brand">
        <img
          class="admin-shell__logo"
          src="@/assets/favicon.svg"
          alt="Glance AI"
          width="36"
          height="36"
        />
        <div>
          <div class="admin-shell__title">Glance AI</div>
          <div class="admin-shell__subtitle">Admin</div>
        </div>
      </div>

      <nav class="admin-shell__nav">
        <router-link
          v-for="item in navItems"
          :key="item.to"
          :to="item.to"
          class="admin-shell__link"
          :class="{ active: isActive(item.to) }"
        >
          {{ item.label }}
        </router-link>
      </nav>

      <div class="admin-shell__actions">
        <div class="admin-shell__call-jump">
          <el-input
            v-model="callId"
            size="default"
            placeholder="Call id"
            clearable
            @keyup.enter="goToCall"
          >
            <template #prefix>
              <el-icon><Search /></el-icon>
            </template>
          </el-input>
          <el-button type="primary" :disabled="!canOpenCall" @click="goToCall">
            Open
          </el-button>
        </div>

        <el-popover
          :disabled="!email"
          placement="bottom-end"
          :width="280"
          trigger="hover"
          :show-after="80"
          :hide-after="80"
          :offset="10"
          popper-class="admin-shell__account-popper"
        >
          <template #reference>
            <span class="admin-shell__avatar-wrap" tabindex="0">
              <el-avatar
                :size="34"
                :src="photoUrl || undefined"
                class="admin-shell__avatar"
              >
                {{ avatarLetter }}
              </el-avatar>
            </span>
          </template>

          <div class="admin-shell__account-card">
            <div class="admin-shell__account-head">
              <el-avatar
                :size="64"
                :src="photoUrlLarge || undefined"
                class="admin-shell__account-photo"
              >
                {{ avatarLetter }}
              </el-avatar>
              <div class="admin-shell__account-identity">
                <div class="admin-shell__account-name">{{ displayName || 'Signed in' }}</div>
                <div v-if="email" class="admin-shell__account-email">{{ email }}</div>
                <div class="admin-shell__account-tags">
                  <span v-if="providerLabel" class="admin-shell__account-chip">{{ providerLabel }}</span>
                  <span
                    v-if="emailVerified"
                    class="admin-shell__account-chip admin-shell__account-chip--ok"
                  >
                    Verified
                  </span>
                </div>
              </div>
            </div>
          </div>
        </el-popover>
        <el-button @click="$emit('sign-out')">Sign out</el-button>
      </div>
    </header>

    <main class="admin-shell__content">
      <slot />
    </main>
  </div>
</template>

<script>
import { Search } from '@element-plus/icons-vue'

export default {
  name: 'AdminShell',
  components: { Search },
  props: {
    account: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['sign-out'],
  data() {
    return {
      callId: '',
      navItems: [
        { to: '/props', label: 'Props' },
        { to: '/main', label: 'Props (v2)' },
        { to: '/calls', label: 'Calls' },
        { to: '/employees', label: 'Employees' },
        { to: '/users', label: 'Users' },
        { to: '/mapping', label: 'Mapping' },
        { to: '/stats/token-usage', label: 'Token usage (Ask GlanceAI)' },
        { to: '/model-configs', label: 'Model Configurations' },
      ],
    }
  },
  computed: {
    email() {
      return this.account?.email || ''
    },
    displayName() {
      return this.account?.displayName || ''
    },
    photoUrl() {
      return this.account?.photoUrl || ''
    },
    photoUrlLarge() {
      const url = this.photoUrl
      if (!url) {
        return ''
      }
      if (/=s\d+/.test(url)) {
        return url.replace(/=s\d+(-c)?/, '=s160$1')
      }
      return url
    },
    emailVerified() {
      return Boolean(this.account?.emailVerified)
    },
    providerLabel() {
      const id = this.account?.providerId || ''
      const labels = {
        'google.com': 'Google',
        'microsoft.com': 'Microsoft',
        password: 'Email & password',
        'apple.com': 'Apple',
        'facebook.com': 'Facebook',
      }
      return labels[id] || (id ? id.replace(/\.com$/, '') : '')
    },
    avatarLetter() {
      const source = this.displayName || this.email || 'U'
      return source.charAt(0).toUpperCase()
    },
    canOpenCall() {
      return String(this.callId || '').trim().length > 0
    },
  },
  methods: {
    isActive(path) {
      const current = this.$route.path
      if (path === '/calls' && current.startsWith('/call/')) {
        return true
      }
      return current === path || current.startsWith(`${path}/`)
    },
    goToCall() {
      const id = String(this.callId || '').trim()
      if (!id) {
        return
      }
      this.$router.push({ name: 'call', params: { id } })
    },
  },
}
</script>

<style scoped>
.admin-shell {
  height: 100vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background:
    radial-gradient(1100px 380px at 8% -8%, rgba(29, 78, 216, 0.12), transparent 55%),
    radial-gradient(800px 320px at 92% 0%, rgba(14, 165, 233, 0.1), transparent 50%),
    var(--admin-bg);
}

.admin-shell__header {
  display: flex;
  align-items: center;
  gap: 18px;
  padding: 10px 20px;
  background: rgba(255, 255, 255, 0.9);
  border-bottom: 1px solid var(--admin-border);
  backdrop-filter: blur(12px);
  position: relative;
  z-index: 20;
  flex-shrink: 0;
}

.admin-shell__brand {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 132px;
}

.admin-shell__logo {
  width: 36px;
  height: 36px;
  object-fit: contain;
  flex-shrink: 0;
  display: block;
}

.admin-shell__title {
  font-weight: 800;
  font-size: 15px;
  line-height: 1.1;
  color: var(--admin-text);
}

.admin-shell__subtitle {
  font-size: 11px;
  color: var(--admin-muted);
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.admin-shell__nav {
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 1;
  min-width: 0;
  overflow-x: auto;
}

.admin-shell__link {
  text-decoration: none;
  color: var(--admin-muted);
  font-weight: 650;
  font-size: 13px;
  padding: 8px 12px;
  border-radius: 999px;
  white-space: nowrap;
  transition: background 0.15s ease, color 0.15s ease;
}

.admin-shell__link:hover {
  background: var(--admin-surface-2);
  color: var(--admin-text);
}

.admin-shell__link.active {
  background: var(--admin-primary-soft);
  color: var(--admin-primary);
}

.admin-shell__actions {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: auto;
}

.admin-shell__call-jump {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 220px;
}

.admin-shell__avatar-wrap {
  display: inline-flex;
  cursor: pointer;
  outline: none;
  border-radius: 999px;
}

.admin-shell__avatar-wrap:hover .admin-shell__avatar,
.admin-shell__avatar-wrap:focus-visible .admin-shell__avatar {
  box-shadow: 0 0 0 3px rgba(29, 78, 216, 0.22);
}

.admin-shell__avatar {
  background: linear-gradient(145deg, #1d4ed8, #0ea5e9);
  color: #fff;
  font-weight: 700;
  transition: box-shadow 0.15s ease;
}

.admin-shell__account-card {
  color: var(--admin-text);
}

.admin-shell__account-head {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 2px 0;
}

.admin-shell__account-photo {
  flex-shrink: 0;
  background: linear-gradient(145deg, #1d4ed8, #0ea5e9);
  color: #fff;
  font-weight: 700;
  font-size: 22px;
  box-shadow: 0 0 0 2px #fff, 0 8px 18px rgba(15, 23, 42, 0.14);
}

.admin-shell__account-identity {
  min-width: 0;
  padding-top: 2px;
}

.admin-shell__account-name {
  font-weight: 750;
  font-size: 15px;
  line-height: 1.25;
  color: var(--admin-text);
}

.admin-shell__account-email {
  margin-top: 3px;
  font-size: 12px;
  color: var(--admin-muted);
  word-break: break-all;
  user-select: text;
}

.admin-shell__account-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

.admin-shell__account-chip {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  color: var(--admin-primary);
  background: var(--admin-primary-soft);
}

.admin-shell__account-chip--ok {
  color: var(--admin-success);
  background: rgba(5, 150, 105, 0.12);
}

.admin-shell__content {
  flex: 1;
  min-height: 0;
  overflow: auto;
  width: 100%;
  box-sizing: border-box;
  /*
   * Full-bleed scroll container so the scrollbar sits against the window edge.
   * The 1440px reading width comes from symmetric padding rather than
   * max-width + auto margins, which would inset the scrollbar with the content.
   */
  padding: 12px max(22px, (100% - 1440px) / 2);
  scrollbar-gutter: stable;
}

@media (max-width: 1100px) {
  .admin-shell__header {
    flex-wrap: wrap;
  }

  .admin-shell__nav {
    order: 3;
    width: 100%;
  }
}
</style>

<style>
.admin-shell__account-popper.el-popper {
  padding: 12px 14px !important;
  border-radius: 14px !important;
  border: 1px solid var(--admin-border) !important;
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.12) !important;
  overflow: hidden;
}

.admin-shell__account-popper .el-popper__arrow::before {
  border: 1px solid var(--admin-border) !important;
  background: #fff !important;
}
</style>
